import { forwardRef } from 'react';
import { Icon, Pill } from '../ui.js';
import { hrefFor } from '../../lib/router.js';
import { OUTCOME_LABEL, REASON_LABEL, label } from '../../lib/labels.js';
import { dateTime, num } from '../../lib/format.js';
import { CHANNEL_LABEL } from '../../lib/labels.js';
import { NODE_INFO, lastAtOf, reasonsFor, ruleFacts, tracePath } from './model.js';
import type { CommTab, FlowData, Kind, NodeLink, Rule } from './model.js';

export type PanelState = { type: 'node'; id: string } | { type: 'trace'; code: string } | null;

function Facts({ rows }: { rows: Array<[string, string | number]> }): JSX.Element {
  return (
    <dl className="fm-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Reasons({
  rows,
}: {
  rows: Array<{ outcome: string; reason: string; n: number }>;
}): JSX.Element {
  if (rows.length === 0)
    return <p className="muted">V tomto období nic neodpadlo — nebo ještě nic neběželo.</p>;
  const top = Math.max(...rows.map((r) => r.n), 1);
  return (
    <ul className="fm-reasons">
      {rows.map((r) => (
        <li key={`${r.outcome}/${r.reason}`}>
          <span>
            <b>{label(REASON_LABEL, r.reason)}</b>
            <small>{label(OUTCOME_LABEL, r.outcome)}</small>
          </span>
          <i
            ref={(el) => {
              el?.style.setProperty('width', `${Math.max(6, (r.n / top) * 100)}%`);
            }}
            aria-hidden="true"
          />
          <em>{num(r.n)}</em>
        </li>
      ))}
    </ul>
  );
}

/** Boční panel: vysvětlení kroku, pravidla, živá čísla a odkazy na obrazovky. */
export const SidePanel = forwardRef<
  HTMLButtonElement,
  {
    state: NonNullable<PanelState>;
    data: FlowData;
    kind: Kind;
    rules: Rule[];
    onClose: () => void;
    onNavigate: (tab: CommTab) => void;
  }
>(function SidePanel({ state, data, kind, rules, onClose, onNavigate }, closeRef) {
  const days = data.days;
  let title: string;
  let body: JSX.Element;

  if (state.type === 'trace') {
    const stat = data.byCode.find((c) => c.code === state.code);
    const trace = tracePath(rules);
    title = `${state.code} — ${stat?.name ?? ''}`;
    const sent = stat?.sent ?? 0;
    body = (
      <>
        <section>
          <h4>Pravidlo z katalogu</h4>
          {rules.length === 0 ? (
            <p className="muted">Kód v publikovaném katalogu nenalezen.</p>
          ) : (
            rules.map((r, i) => (
              <div key={i} className="fm-rule">
                {rules.length > 1 ? (
                  <b>
                    Varianta {i + 1}: {r.name}
                  </b>
                ) : null}
                <Facts rows={ruleFacts(r, data.settings)} />
              </div>
            ))
          )}
          {trace.ends ? (
            <p className="note">
              Tuto zprávu posílá e-shop. Hub ji jen eviduje a používá jako událost, proto cesta
              končí u cest zákazníků.
            </p>
          ) : null}
        </section>
        <section>
          <h4>Čísla za posledních {days} dní</h4>
          <Facts
            rows={[
              ['Na řadě', num(stat?.pending ?? 0)],
              ['Odesláno', num(sent)],
              ['Doručeno', num(stat?.delivered ?? 0)],
              ['Otevřeno', num(stat?.opened ?? 0)],
              ['Kliknuto', num(stat?.clicked ?? 0)],
              ['Odhlášeno', num(stat?.unsubscribed ?? 0)],
              ['Sloučeno', num(stat?.merged ?? 0)],
              ['Podrženo', num(stat?.held ?? 0)],
              ['Odloženo', num(stat?.deferred ?? 0)],
              ['Potlačeno', num(stat?.suppressed ?? 0)],
              ['Propadlo', num(stat?.expired ?? 0)],
            ]}
          />
        </section>
        <section>
          <h4>Proč se nepošle</h4>
          <Reasons rows={reasonsFor(data, 'all', state.code)} />
        </section>
        <Links
          links={[
            { text: 'Pravidlo v Toku zpráv', tab: 'flow' },
            { text: 'Šablona kódu', tab: 'editor' },
            { text: 'Přehled zpráv a vypínač', tab: 'catalogue' },
          ]}
          onNavigate={onNavigate}
        />
      </>
    );
  } else {
    const info = NODE_INFO[state.id];
    if (!info) return <></>;
    title = info.title;
    const last = lastAtOf(state.id, data);
    const showReasons = state.id === 'decisions';
    body = (
      <>
        <section>
          <h4>Co se tu děje</h4>
          <p>{info.what}</p>
        </section>
        <section>
          <h4>Proč na tom záleží</h4>
          <p>{info.why}</p>
        </section>
        <section>
          <h4>Pravidla</h4>
          <ul className="fm-list">
            {info.rules(data.settings).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </section>
        <section>
          <h4>
            Živá čísla za {days} dní
            {kind !== 'all' ? ` · ${label(CHANNEL_LABEL, kind)}` : ''}
          </h4>
          <Facts rows={info.facts(data, kind)} />
          <p className="muted">Poslední aktivita: {last ? dateTime(last) : '—'}</p>
        </section>
        {showReasons ? (
          <section>
            <h4>Proč se zprávy neodeslaly</h4>
            <Reasons rows={reasonsFor(data, kind)} />
            <p className="muted">
              Každé zadržení, odložení nebo potlačení má v databázi zapsaný důvod.
            </p>
          </section>
        ) : null}
        <Links links={info.links} onNavigate={onNavigate} />
      </>
    );
  }

  return (
    <aside className="fm-drawer" role="dialog" aria-label={`Podrobnosti: ${title}`}>
      <header>
        <h3>{title}</h3>
        {state.type === 'node' && NODE_INFO[state.id] && ['src_se', 'react'].includes(state.id) ? (
          <Pill tone="warn">připravuje se</Pill>
        ) : null}
        <button ref={closeRef} className="fm-x" onClick={onClose} aria-label="Zavřít panel">
          <Icon name="close" size={18} />
        </button>
      </header>
      <div className="fm-drawer-body">{body}</div>
    </aside>
  );
});

function Links({
  links,
  onNavigate,
}: {
  links: NodeLink[];
  onNavigate: (tab: CommTab) => void;
}): JSX.Element {
  return (
    <section>
      <h4>Kam dál</h4>
      <div className="fm-links">
        {links.map((l) =>
          l.tab ? (
            <button key={l.text} className="btn" onClick={() => onNavigate(l.tab as CommTab)}>
              {l.text}
              <Icon name="chevron" size={14} />
            </button>
          ) : (
            <a key={l.text} className="btn" href={hrefFor(l.screen ?? 'overview')}>
              {l.text}
              <Icon name="chevron" size={14} />
            </a>
          ),
        )}
      </div>
    </section>
  );
}
